import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { examKey, formatInstantAsTashkentDay, levelKey, subjectKey } from 'modules/studyPlan/labels';
import type { GoalPlan } from 'modules/studyPlan';

function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-display text-[26px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</span>
      <span className="text-[12.5px] leading-snug text-muted-foreground">{label}</span>
    </div>
  );
}

interface GoalCardProps {
  goals: GoalPlan[];
  /** Days to the nearest exam. */
  daysLeft: number | null;
  dailyStudyMinutes: number;
  targetPercent: number;
  /** Opens the goal form, at an exam whose answer is missing when one is given. */
  onEdit: (exam?: string) => void;
}

/** What the learner is working towards: each exam with its subjects, level and date, then what they share. */
export function GoalCard({ goals, daysLeft, dailyStudyMinutes, targetPercent, onEdit }: GoalCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language ?? 'en';
  const several = goals.length > 1;

  const examOf = (goal: GoalPlan) => t(examKey(goal.exam), { defaultValue: goal.exam });
  const subjectsOf = (goal: GoalPlan) => goal.subjects.map((s) => t(subjectKey(s), { defaultValue: s })).join(' + ');
  const dateOf = (goal: GoalPlan) =>
    goal.daysLeft !== null && goal.daysLeft < 0
      ? t('studyPlan.exam_passed')
      : goal.examDate
        ? t('studyPlan.exam_on', { date: formatInstantAsTashkentDay(goal.examDate, language, { day: 'numeric', month: 'long', year: 'numeric' }) })
        : t('studyPlan.exam_date_unknown');

  return (
    <section aria-labelledby="plan-goal" className="rounded-lg border border-border bg-card p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p id={several ? 'plan-goal' : undefined} className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t(several ? 'studyPlan.goals_eyebrow' : 'studyPlan.goal_eyebrow')}
        </p>
        <Button variant="outline" size="sm" onClick={() => onEdit()}>
          <Pencil size={14} className="mr-1.5" aria-hidden="true" />
          {t('studyPlan.edit_goal')}
        </Button>
      </div>

      <ul className={cn('mt-2.5', several && 'divide-y divide-border')}>
        {goals.map((goal) => {
          const subjects = subjectsOf(goal);
          const title = subjects ? `${examOf(goal)} · ${subjects}` : examOf(goal);
          return (
            <li key={goal.exam} className={cn(several && 'py-3 first:pt-0 last:pb-0')}>
              {several ? (
                <h3 className="text-[17px] font-bold leading-snug text-foreground">{title}</h3>
              ) : (
                <h2 id="plan-goal" className="font-display text-2xl font-bold leading-tight tracking-[-0.02em] text-foreground">{title}</h2>
              )}
              {goal.level && <p className="mt-1 text-sm text-muted-foreground">{t(levelKey(goal.level), { defaultValue: goal.level })}</p>}
              {several && <p className="mt-0.5 text-[13px] text-muted-foreground">{dateOf(goal)}</p>}
              {goal.gaps.length > 0 && (
                <div className="mt-3 flex flex-col gap-3 rounded-md bg-muted p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{t('studyPlan.gap_title', { exam: examOf(goal) })}</p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{t(`studyPlan.gap_${goal.gaps[0]}`)}</p>
                  </div>
                  <Button size="sm" onClick={() => onEdit(goal.exam)} className="shrink-0">{t('studyPlan.gap_action')}</Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5 border-t border-border pt-5 md:grid-cols-4">
        <Stat value={daysLeft !== null && daysLeft >= 0 ? daysLeft : '—'} label={t(several ? 'studyPlan.stat_days_nearest' : 'studyPlan.stat_days')} />
        <Stat value={dailyStudyMinutes} label={t('studyPlan.stat_minutes')} />
        <Stat value={`${targetPercent}%`} label={t('studyPlan.stat_target')} />
        {!several && (
          <Stat value={goals[0]?.mastery.overall == null ? '—' : `${goals[0].mastery.overall}%`} label={t('studyPlan.stat_mastery')} />
        )}
      </div>
      {!several && goals[0] && <p className="mt-4 text-[13px] text-muted-foreground">{dateOf(goals[0])}</p>}
    </section>
  );
}

export default GoalCard;
